var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, } from "@mui/material";
import { CaretDown, PencilSimple } from "@phosphor-icons/react";
import { AuthenticationContext } from "../../../context/AuthenticationContext";
import UsersApi from "../../../infrastructure/api/pages/users-api";
import "./UserMenu.scss";
const deriveInitials = (name) => {
    if (!name)
        return "";
    return name
        .trim()
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("");
};
export const UserMenu = () => {
    const { getProfile, initials, setInitials, account } = useContext(AuthenticationContext);
    const { t: translate } = useTranslation();
    // Read during render, not in a mount effect. Every page renders its own
    // <Layout>, so navigating remounts this component — an effect would leave the
    // name blank for one frame each time, which read as the block "reloading".
    // getProfile() is a synchronous jwt_decode of the stored token.
    const user = useMemo(() => getProfile(), []);
    const [open, setOpen] = useState(false);
    const [initialsDialogOpen, setInitialsDialogOpen] = useState(false);
    const [initialsInput, setInitialsInput] = useState("");
    const [savingInitials, setSavingInitials] = useState(false);
    const rootRef = useRef(null);
    const triggerRef = useRef(null);
    const menuRef = useRef(null);
    const menuItems = () => {
        var _a, _b;
        return Array.from((_b = (_a = menuRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll("[data-menuitem]")) !== null && _b !== void 0 ? _b : []);
    };
    const close = (returnFocus = true) => {
        var _a;
        setOpen(false);
        if (returnFocus)
            (_a = triggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    };
    // Listeners exist only while the menu is open — nothing is bound on hover.
    useEffect(() => {
        var _a;
        if (!open)
            return;
        (_a = menuItems()[0]) === null || _a === void 0 ? void 0 : _a.focus();
        const onPointerDown = (event) => {
            var _a;
            if (!((_a = rootRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target)))
                setOpen(false);
        };
        const onKeyDown = (event) => {
            var _a, _b, _c, _d;
            const items = menuItems();
            const index = items.indexOf(document.activeElement);
            switch (event.key) {
                case "Escape":
                    event.preventDefault();
                    close();
                    break;
                case "ArrowDown":
                    event.preventDefault();
                    (_a = items[(index + 1) % items.length]) === null || _a === void 0 ? void 0 : _a.focus();
                    break;
                case "ArrowUp":
                    event.preventDefault();
                    (_b = items[(index - 1 + items.length) % items.length]) === null || _b === void 0 ? void 0 : _b.focus();
                    break;
                case "Home":
                    event.preventDefault();
                    (_c = items[0]) === null || _c === void 0 ? void 0 : _c.focus();
                    break;
                case "End":
                    event.preventDefault();
                    (_d = items[items.length - 1]) === null || _d === void 0 ? void 0 : _d.focus();
                    break;
                case "Tab":
                    setOpen(false);
                    break;
                default:
                    break;
            }
        };
        document.addEventListener("pointerdown", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("pointerdown", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [open]);
    // Item #1 — every user may edit their own Initials (used by the object
    // timestamp feature). It used to hide behind a chip that read as an avatar.
    const openInitialsDialog = () => {
        setInitialsInput(initials);
        setInitialsDialogOpen(true);
        close(false);
    };
    const saveInitials = () => __awaiter(void 0, void 0, void 0, function* () {
        const trimmed = initialsInput.trim();
        setSavingInitials(true);
        try {
            yield UsersApi.updateOwnInitials({ initials: trimmed });
            setInitials(trimmed);
            setInitialsDialogOpen(false);
            toast.success(translate("UpdatedSuccessfully"));
        }
        catch (error) {
            toast.error(translate("SomethingWentWrong"));
        }
        finally {
            setSavingInitials(false);
        }
    });
    // Prefer the live record from the API; fall back to the JWT claims until it
    // arrives, so nothing ever renders blank.
    const name = account.name || user.name;
    const email = account.email || user.emailaddress;
    const badge = initials || deriveInitials(name);
    return (_jsxs("div", { className: "eds-user", ref: rootRef, children: [_jsxs("button", { type: "button", ref: triggerRef, className: "eds-user__trigger", "aria-haspopup": "menu", "aria-expanded": open, "aria-controls": "eds-user-menu", onClick: () => setOpen((value) => !value), onKeyDown: (event) => {
                    if (event.key === "ArrowDown" && !open) {
                        event.preventDefault();
                        setOpen(true);
                    }
                }, children: [_jsx("span", { className: "eds-user__avatar", "aria-hidden": "true", children: badge }), _jsx("span", { className: "eds-user__name", children: name }), _jsx("span", { className: "eds-user__caret", "data-open": open || undefined, children: _jsx(CaretDown, { size: 14, weight: "bold" }) })] }), _jsxs("div", { id: "eds-user-menu", className: "eds-user__menu", role: "menu", "aria-label": name || "Account", "data-open": open || undefined, "aria-hidden": open ? undefined : true, ref: menuRef, children: [_jsxs("div", { className: "eds-user__header", children: [_jsx("span", { className: "eds-user__avatar eds-user__avatar--lg", "aria-hidden": "true", children: badge }), _jsxs("span", { className: "eds-user__id", children: [_jsx("strong", { children: name }), _jsx("span", { title: email, children: email }), (user === null || user === void 0 ? void 0 : user.role) && _jsx("em", { children: user.role })] })] }), _jsxs("button", { type: "button", role: "menuitem", "data-menuitem": true, tabIndex: -1, className: "eds-user__item", onClick: openInitialsDialog, children: [_jsx(PencilSimple, { size: 17 }), _jsx("span", { children: translate("Mijn initialen") }), _jsx("span", { className: "eds-user__value", children: initials || translate("Stel initialen in") })] })] }), _jsxs(Dialog, { open: initialsDialogOpen, onClose: () => setInitialsDialogOpen(false), children: [_jsx(DialogTitle, { children: translate("Mijn initialen") }), _jsx(DialogContent, { children: _jsx(TextField, { autoFocus: true, margin: "dense", label: translate("Initials"), fullWidth: true, variant: "outlined", value: initialsInput, onChange: (event) => setInitialsInput(event.target.value), slotProps: {
                                htmlInput: { maxLength: 5 }
                            } }) }), _jsxs(DialogActions, { children: [_jsx(Button, { onClick: () => setInitialsDialogOpen(false), children: translate("Cancel") }), _jsx(Button, { variant: "contained", disabled: savingInitials, onClick: saveInitials, children: translate("Save") })] })] })] }));
};
export default UserMenu;
